import { RunView } from '@memberjunction/core';
import { AgentStepType, PipelineStepConfig, PipelineConfig } from '../../types';

// ─── Icon and color assignment ─────────────────────
// Maps keywords in agent names to visual identifiers.
// Agents not matching any keyword get a default style.
const AGENT_VISUALS: { keyword: string; icon: string; color: string }[] = [
    { keyword: 'Pipeline',     icon: 'fa-wand-magic-sparkles', color: 'var(--mj-color-indigo-500)' },
    { keyword: 'Newsletter',   icon: 'fa-envelope',            color: 'var(--mj-color-info-500)' },
    { keyword: 'Social',       icon: 'fa-share-nodes',         color: 'var(--mj-status-warning)' },
    { keyword: 'Podcast',      icon: 'fa-podcast',             color: 'var(--mj-color-violet-500)' },
    { keyword: 'Repurposing',  icon: 'fa-recycle',             color: 'var(--mj-status-success)' },
    { keyword: 'Performance',  icon: 'fa-chart-line',          color: 'var(--mj-status-error)' },
    { keyword: 'Brand Voice',  icon: 'fa-bullhorn',            color: 'var(--mj-color-violet-400)' },
];
const DEFAULT_ICON = 'fa-robot';
const DEFAULT_COLOR = 'var(--mj-color-indigo-500)';

function resolveVisuals(agentName: string): { icon: string; color: string } {
    const match = AGENT_VISUALS.find(v => agentName.includes(v.keyword));
    return match ? { icon: match.icon, color: match.color } : { icon: DEFAULT_ICON, color: DEFAULT_COLOR };
}

// ─── Dynamic agent discovery ───────────────────────

/** Fetches marketing agents from DB and builds the step type registry */
export async function loadAgentStepTypes(): Promise<AgentStepType[]> {
    const rv = new RunView();
    const result = await rv.RunView({
        EntityName: 'MJ: AI Agents',
        ExtraFilter: `Name LIKE 'Marketing%' AND Status='Active'`,
        OrderBy: 'Name',
        ResultType: 'simple',
        Fields: ['ID', 'Name', 'Description'],
    });

    if (!result.Success || !result.Results?.length) return [];

    const agents = result.Results as Record<string, unknown>[];

    // Exclude the CMO itself — it's the orchestrator, not a composable step
    return agents
        .filter(a => !(a['Name'] as string).includes('CMO'))
        .map(a => {
            const name = a['Name'] as string;
            const { icon, color } = resolveVisuals(name);
            // Strip "Marketing " prefix for display
            const displayName = name.replace(/^Marketing\s+/, '').replace(/\s+Agent$/, '');
            return {
                agentName: name,
                displayName,
                icon,
                color,
                description: (a['Description'] as string) || '',
                hasTemplate: name.includes('Newsletter'), // Newsletter uses content templates
                templateType: name.includes('Newsletter') ? 'Newsletter' : undefined,
                instructionsHint: `Custom instructions for the ${displayName} step...`,
            };
        });
}

/** Create a default source step for new pipelines */
export function createSourceStep(): PipelineStepConfig {
    return {
        id: crypto.randomUUID(),
        type: 'source',
        displayName: 'Source',
        icon: 'fa-file-lines',
        color: 'var(--mj-color-indigo-500)',
        instructions: '',
        payloadOverrides: {},
    };
}

/** Create an agent step from a registry entry */
export function createAgentStep(stepType: AgentStepType): PipelineStepConfig {
    return {
        id: crypto.randomUUID(),
        type: 'agent',
        agentName: stepType.agentName,
        displayName: stepType.displayName,
        icon: stepType.icon,
        color: stepType.color,
        instructions: '',
        templateType: stepType.templateType,
        payloadOverrides: {},
    };
}

/** Build enriched instructions by combining user instructions with upstream context */
/** Extract piping data from a completed agent run for the next step */
export function extractPipingData(agentName: string, runOutput: Record<string, unknown>): Record<string, unknown> {
    const data: Record<string, unknown> = { lastStepAgent: agentName };

    if (runOutput['newsletterId']) {
        data['newsletterId'] = runOutput['newsletterId'];
        data['newsletterSubject'] = runOutput['subject'] || runOutput['newsletterSubject'] || '';
    }
    if (runOutput['socialPostIds']) {
        data['socialPostIds'] = runOutput['socialPostIds'];
        data['socialPostCount'] = Array.isArray(runOutput['socialPostIds']) ? (runOutput['socialPostIds'] as unknown[]).length : 0;
    }
    if (runOutput['blogIds'] || runOutput['generatedBlogIds']) {
        const ids = (runOutput['blogIds'] || runOutput['generatedBlogIds']) as string[];
        data['generatedBlogIds'] = ids;
        data['generatedBlogTitles'] = runOutput['blogTitles'] || runOutput['generatedBlogTitles'] || [];
        data['generatedBlogCount'] = ids?.length || 0;
    }

    return data;
}

/** localStorage persistence */
const STORAGE_KEY = 'cmo-pipelines';

export function savePipelineConfigs(configs: PipelineConfig[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
}

export function loadPipelineConfigs(): PipelineConfig[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
}
